import { Outlet } from "react-router-dom";
import { createContext, useContext, useEffect, useState } from "react";
import { GlobalModal } from "components/common/Modal";

interface ModalState {
  isVisible: boolean;
  type: 'alert' | 'confirm';
  message: string;
  onConfirm?: () => void; // 확인 버튼 콜백
  onCancel?: () => void; // 취소 버튼 콜백
  // CAUTION: 앱 브릿지로 링크 이동 여부. 앱 브릿지시 true로 설정해 주세요. showModal에서 true로 설정할 경우, Modal.tsx에서 hideModal 동작을 수행하지 않게 합니다.
  appLink?: {
    confirm: boolean;
    cancel: boolean;
  };
}

const initialState: ModalState = {
  isVisible: false,
  type: 'alert',
  message: '',
  appLink: {
    confirm: false,
    cancel: false,
  }, // 기본적으로 AppLink 가 아니라고 설정
};

const ModalContext = createContext<{
  modalState: ModalState;
  showModal: (type: 'alert' | 'confirm', message: string, appLink?: { confirm: boolean; cancel: boolean; }, onConfirm?: () => void, onCancel?: () => void) => void;
  hideModal: () => void;
}>({
  modalState: initialState,
  showModal: () => {},
  hideModal: () => {},
});

function ModalLayout() {
  const [modalState, setModalState] = useState<ModalState>(initialState);

  // 모달 열기
  const showModal = (type: 'alert' | 'confirm', message: string, appLink?: { confirm: boolean; cancel: boolean; }, onConfirm?: () => void, onCancel?: () => void) => {
    if (!modalState.isVisible) {
      // 현재 모달이 열려 있지 않을 때만 히스토리 상태 추가
      window.history.pushState({ modalOpen: true }, '');
    }

    setModalState({
      isVisible: true,
      type,
      message,
      onConfirm,
      onCancel: onCancel, // 기본적으로 hideModal을 취소 핸들러로 설정
      appLink: appLink === undefined || appLink === null ? initialState.appLink : appLink,
    });
  };

  // 모달 닫기
  const hideModal = () => {
    setModalState(initialState);
    if (window.history.state?.modalOpen) {
        window.history.back(); // 히스토리 상태 복구
    }
  };

  // 뒤로가기 이벤트 처리
  useEffect(() => {
    const handlePopState = () => {
      if (modalState.isVisible) {
        hideModal();
      }
    };

    window.addEventListener('popstate', handlePopState);

    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [modalState.isVisible]);

  return (
    <ModalContext.Provider value={{ modalState, showModal, hideModal }}>
      <Outlet />
      {modalState.isVisible && (
        <GlobalModal />
      )}
    </ModalContext.Provider>
  );
}

export default ModalLayout;
export const useModal = () => useContext(ModalContext);